import React from 'react'

/**
 * Memory Management Utilities for AdminFront
 * 메모리 최적화를 위한 유틸리티 함수들
 */

// 가비지 컬렉션 강제 실행 (개발 환경에서만)
export const forceGarbageCollection = () => {
  if (process.env.NODE_ENV === 'development' && (window as any).gc) {
    try {
      (window as any).gc()
      console.log('🗑️ 가비지 컬렉션 실행됨')
    } catch (e) {
      console.warn('가비지 컬렉션 실행 실패:', e)
    }
  }
}

// localStorage 크기 체크 및 정리
export const cleanupLocalStorage = () => {
  try {
    const keys = Object.keys(localStorage)
    let totalSize = 0
    
    keys.forEach(key => {
      const value = localStorage.getItem(key) || ''
      totalSize += key.length + value.length
    })
    
    // 5MB 이상이면 정리 (5 * 1024 * 1024 = 5242880)
    if (totalSize > 5242880) {
      // app-cache만 정리 (가장 큰 데이터)
      const appCache = localStorage.getItem('app-cache')
      if (appCache) {
        try {
          const parsed = JSON.parse(appCache)
          const reduced = parsed.slice(-30) // 최신 30개만 유지
          localStorage.setItem('app-cache', JSON.stringify(reduced))
          console.log('🧹 localStorage 정리됨: app-cache 축소')
        } catch (e) {
          localStorage.removeItem('app-cache')
          console.log('🧹 localStorage 정리됨: app-cache 제거')
        }
      }
      
      // 오래된 임시 데이터 정리
      keys.forEach(key => {
        if (key.startsWith('temp_') || key.startsWith('cache_')) {
          localStorage.removeItem(key)
        }
      })
    }
    
    return { totalSize, cleaned: totalSize > 5242880 }
  } catch (error) {
    console.error('localStorage 정리 중 오류:', error)
    return { totalSize: 0, cleaned: false }
  }
}

// 메모리 사용량 체크 (Chrome DevTools 지원)
export const getMemoryInfo = () => {
  if ('memory' in performance && (performance as any).memory) {
    const memory = (performance as any).memory
    return {
      usedJSHeapSize: Math.round(memory.usedJSHeapSize / 1048576), // MB
      totalJSHeapSize: Math.round(memory.totalJSHeapSize / 1048576), // MB
      jsHeapSizeLimit: Math.round(memory.jsHeapSizeLimit / 1048576), // MB
      usage: Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100) // %
    }
  }
  return null
}

// 페이지 언로드 시 정리 작업
export const cleanupOnUnload = () => {
  // SWR 캐시 정리
  cleanupLocalStorage()
  
  // 타이머 정리는 브라우저 환경에서는 제한적이므로 주석 처리
  // const highestTimeoutId = setTimeout(() => {}, 0)
  // for (let i = 0; i < highestTimeoutId; i++) {
  //   clearTimeout(i)
  // }
  
  // const highestIntervalId = setInterval(() => {}, 0)
  // for (let i = 0; i < highestIntervalId; i++) {
  //   clearInterval(i)
  // }
  
  console.log('🧹 페이지 언로드 정리 완료')
}

// 자동 메모리 관리 시작
export const startMemoryManager = () => {
  // 5분마다 메모리 체크 및 정리
  const interval = setInterval(() => {
    const memInfo = getMemoryInfo()
    
    if (memInfo && memInfo.usage > 80) {
      console.warn(`⚠️ 메모리 사용량 높음: ${memInfo.usage}% (${memInfo.usedJSHeapSize}MB/${memInfo.jsHeapSizeLimit}MB)`)
      
      // 정리 작업 수행
      cleanupLocalStorage()
      forceGarbageCollection()
      
      console.log('🧹 자동 메모리 정리 실행됨')
    } else if (memInfo) {
      console.log(`💚 메모리 상태 양호: ${memInfo.usage}% (${memInfo.usedJSHeapSize}MB/${memInfo.jsHeapSizeLimit}MB)`)
    }
  }, 5 * 60 * 1000) // 5분
  
  // 페이지 언로드시 정리
  window.addEventListener('beforeunload', cleanupOnUnload)
  
  return () => {
    clearInterval(interval)
    window.removeEventListener('beforeunload', cleanupOnUnload)
  }
}

// React 컴포넌트에서 사용할 hook
export const useMemoryManager = () => {
  React.useEffect(() => {
    const cleanup = startMemoryManager()
    
    return cleanup
  }, [])
  
  return {
    forceCleanup: () => {
      cleanupLocalStorage()
      forceGarbageCollection()
    },
    getMemoryInfo,
    cleanupLocalStorage
  }
}